const normalizeReference = (value) => {
  if (typeof value !== 'string') return ''

  try {
    value = decodeURIComponent(value)
  } catch {}

  return value
    .replace(/^#/, '')
    .replace(/^['"]|['"]$/g, '')
    .trim()
    .toLowerCase()
}

const toHandle = (value) => normalizeReference(value)
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '')

export const guideReferenceMatches = (reference, candidates) => {
  const normalizedReference = normalizeReference(reference)
  const referenceHandle = toHandle(reference)

  if (!normalizedReference) return false

  return candidates.some((candidate) => {
    const normalizedCandidate = normalizeReference(candidate)
    const candidateHandle = toHandle(candidate)
    const handlesMatch = referenceHandle && candidateHandle && (
      candidateHandle === referenceHandle ||
      candidateHandle.includes(referenceHandle) ||
      referenceHandle.includes(candidateHandle)
    )

    if (!normalizedCandidate) return false

    return normalizedCandidate === normalizedReference ||
      handlesMatch ||
      (normalizedReference.length >= 3 && (
        normalizedCandidate.includes(normalizedReference) ||
        normalizedReference.includes(normalizedCandidate)
      ))
  })
}

if (typeof window !== 'undefined') {
  class LyleProductSizeGuide extends HTMLElement {
    #loaded = false
    #loading = false
    #popover = null

    connectedCallback() {
      this.#popover = this.querySelector('[popover]')

      this.#popover?.addEventListener('toggle', (event) => {
        if (event.newState === 'open') this.#load()
      })

      // A bundle item guide waits for its component data, then loads if it was
      // opened in the meantime.
      this.addEventListener('size-guide:ready', () => {
        if (this.#popover?.matches(':popover-open')) this.#load()
      })

      this.addEventListener('click', (event) => {
        const unitButton = event.target.closest('[data-size-guide-unit]')
        if (unitButton) this.#setUnit(unitButton.dataset.sizeGuideUnit)

        if (event.target.closest('[data-size-guide-unit-toggle]')) {
          const activeUnit = this.querySelector('[data-size-guide-unit][aria-pressed="true"]')?.dataset.sizeGuideUnit
          this.#setUnit(activeUnit === 'cm' ? 'in' : 'cm')
        }
      })
    }

    async #load() {
      if (this.#loaded || this.#loading || this.hasAttribute('data-guide-pending')) return

      this.#loading = true

      try {
        const sourceUrl = new URL(this.dataset.url, window.location.href)
        const response = await fetch(`${sourceUrl.pathname}${sourceUrl.search}`, {
          credentials: 'same-origin',
        })

        if (!response.ok) throw new Error(`Size guide request failed: ${response.status}`)

        const documentFragment = new DOMParser().parseFromString(
          await response.text(),
          'text/html',
        )
        const sections = [...documentFragment.querySelectorAll('[data-size-guides-section]')]

        if (!sections.length) throw new Error('Size guide section not found')

        const categoryReference = sourceUrl.hash
        let category = sections.find((section) => guideReferenceMatches(
          categoryReference,
          [section.dataset.sizeGuidesSection],
        )) || sections[0]
        let blocks = [...category.querySelectorAll('[data-size-guides-title]')]
        const allBlocks = sections.flatMap((section) => [
          ...section.querySelectorAll('[data-size-guides-title]'),
        ])
        const guideReference = this.dataset.guideReference
        let measurementGuide = blocks.find((block) => (
          block.querySelector('lyle-csv-table') &&
          guideReferenceMatches(guideReference, this.#guideCandidates(block))
        ))

        if (!measurementGuide && guideReference) {
          measurementGuide = allBlocks.find((block) => (
            block.querySelector('lyle-csv-table') &&
            guideReferenceMatches(guideReference, this.#guideCandidates(block))
          ))

          if (measurementGuide) {
            category = measurementGuide.closest('[data-size-guides-section]')
            blocks = [...category.querySelectorAll('[data-size-guides-title]')]
          }
        }

        measurementGuide ||= blocks.find((block) => block.querySelector('lyle-csv-table'))

        if (!measurementGuide) throw new Error('Size guide table not found')

        const contentGuide = blocks.find((block) => (
          block !== measurementGuide &&
          block.querySelector('.size-guide-item__content')
        )) || measurementGuide

        this.#renderGuide(measurementGuide, contentGuide)
        this.#loaded = true
      } catch (error) {
        console.error('Product size guide: unable to load data', error)
        this.querySelector('[data-size-guide-loading]').hidden = true
        this.querySelector('[data-size-guide-error]').hidden = false
      } finally {
        this.#loading = false
      }
    }

    #guideCandidates(block) {
      return [
        block.dataset.sizeGuidesTitle,
        block.dataset.sizeGuidesTitleLabel,
        ...[...block.querySelectorAll('lyle-csv-table')].map((table) => table.dataset.url),
      ]
    }

    #renderGuide(measurementGuide, contentGuide) {
      const title = measurementGuide.dataset.sizeGuidesTitleLabel
      const titleElement = this.#popover?.querySelector('.popup-drawer__title')

      if (title && titleElement) {
        titleElement.textContent = `${title} ${this.dataset.title}`
      }

      const availableUnits = ['cm', 'in'].filter((unit) => {
        const table = measurementGuide.querySelector(
          `[data-size-guides-unit-panel="${unit}"] lyle-csv-table`,
        )
        const panel = this.querySelector(`[data-size-guide-panel="${unit}"]`)

        panel.replaceChildren()
        panel.hidden = !table
        if (table) panel.appendChild(table.cloneNode(true))

        return Boolean(table)
      })

      const howToContent = contentGuide.querySelector('.size-guide-item__content')
      const howToSection = this.querySelector('[data-size-guide-how-to]')
      const howToTarget = this.querySelector('[data-size-guide-how-to-content]')

      if (howToContent) {
        howToTarget.replaceChildren(howToContent.cloneNode(true))
        howToSection.hidden = false
      }

      this.querySelector('[data-size-guide-unit-controls]').hidden = availableUnits.length < 2
      this.querySelector('[data-size-guide-loading]').hidden = true
      this.querySelector('[data-size-guide-content]').hidden = false
      this.#setUnit(availableUnits.includes('cm') ? 'cm' : availableUnits[0])
    }

    #setUnit(unit) {
      if (!unit) return

      for (const panel of this.querySelectorAll('[data-size-guide-panel]')) {
        panel.hidden = panel.dataset.sizeGuidePanel !== unit || !panel.children.length
      }

      for (const button of this.querySelectorAll('[data-size-guide-unit]')) {
        const active = button.dataset.sizeGuideUnit === unit
        button.classList.toggle('is-active', active)
        button.setAttribute('aria-pressed', active.toString())
      }

      this.querySelector('[data-size-guide-unit-toggle]')
        ?.classList.toggle('is-cm', unit === 'cm')

      requestAnimationFrame(() => {
        this.querySelector(`[data-size-guide-panel="${unit}"] lyle-csv-table`)
          ?.refreshScrollbar?.()
      })
    }
  }

  if (!customElements.get('lyle-product-size-guide')) {
    customElements.define('lyle-product-size-guide', LyleProductSizeGuide)
  }
}
